/**
 * @spec Figma Design System - Dropdown List Item (node-id: 22961:92539)
 * Reusable dropdown item component with default, hover, and disabled states
 */
import { type ReactNode, type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

export type DropdownItemState = 'default' | 'hover' | 'disabled' | 'selected';

export interface DropdownItemProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  /** Primary content/label */
  children: ReactNode;
  /** Optional icon to display before the label */
  icon?: ReactNode;
  /** Optional secondary label (displayed after primary, muted) */
  secondaryLabel?: string;
  /** Optional trailing content (e.g., checkmark for selected state) */
  trailing?: ReactNode;
  /** Whether the item is selected */
  selected?: boolean;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Whether this is the first item (rounds top corners) */
  isFirst?: boolean;
  /** Whether this is the last item (rounds bottom corners) */
  isLast?: boolean;
}

/**
 * Dropdown item following Figma design system
 *
 * States (from Figma node-id: 22961:92539):
 * - Default: 50% white text, no background
 * - Hover: 100% white text, 3% white background
 * - Disabled: 30% white text, cursor not allowed
 * - Selected: 100% white text (same as hover visually)
 *
 * Dimensions:
 * - Height: 44px
 * - Horizontal padding: 16px
 * - Gap between elements: 8px
 * - Font: Manrope SemiBold, 14px, line-height 20px
 */
export const DropdownItem = forwardRef<HTMLButtonElement, DropdownItemProps>(function DropdownItem(
  {
    children,
    icon,
    secondaryLabel,
    trailing,
    selected = false,
    disabled = false,
    isFirst = false,
    isLast = false,
    className,
    ...props
  },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cn(
        // Base styles - Figma spec: h-44px, px-16px, gap-8px
        'w-full h-[44px] flex items-center gap-2 px-4',
        'text-left transition-colors',
        // Typography - Figma spec: Manrope SemiBold 14px/20px
        'font-semibold text-sm leading-5',
        // Rounded corners for first/last items to match container
        isFirst && 'rounded-t-lg',
        isLast && 'rounded-b-lg',
        // State styles from Figma
        disabled
          ? // Disabled: 30% white, no hover effect
            'text-white/30 cursor-not-allowed'
          : selected
            ? // Selected: 100% white (matches hover)
              'text-white'
            : // Default: 50% white, hover shows 100% white + 3% bg
              'text-white/50 hover:text-white hover:bg-white/[0.03]',
        className
      )}
      aria-disabled={disabled || undefined}
      {...props}
    >
      {/* Optional leading icon */}
      {icon && (
        <span className="flex-shrink-0 size-5 flex items-center justify-center">{icon}</span>
      )}

      {/* Primary label */}
      <span className="flex-1 truncate">{children}</span>

      {/* Optional secondary label */}
      {secondaryLabel && (
        <span className="text-white/30 text-xs font-normal flex-shrink-0">{secondaryLabel}</span>
      )}

      {/* Optional trailing content (e.g., checkmark) */}
      {trailing && <span className="flex-shrink-0">{trailing}</span>}
    </button>
  );
});
